import { CardData, GameState, LastMoveType, QueueCard, SlotCard } from '../types/types';
import { animateCardToSlot } from './cardAnimations';
import { generateLevel } from './levelGenerator';

// Maximum number of slots available
export const MAX_SLOTS = 7;

/**
 * Initialize or reset the game
 * @returns {GameState} Initial game state
 */
export const startNewGame = (): GameState => {
  const initialGameState = generateLevel();
  
  // Add properties needed for gameplay
  initialGameState.lastMove = null;
  initialGameState.canRevert = false;
  initialGameState.usedRevert = false;
  initialGameState.usedReshuffle = false;
  initialGameState.usedMoveOut = false;
  
  console.log('Initial game state:', initialGameState);
  return initialGameState;
};

/**
 * Handle when a card on the board is clicked
 * @param clickedCard - The card that was clicked
 * @param gameState - Current game state
 * @param setGameState - Function to update game state
 * @param slotsRef - Reference to the slots container
 */
export const handleCardClick = (
  clickedCard: CardData,
  gameState: GameState,
  setGameState: (state: GameState | ((prevState: GameState) => GameState)) => void,
  slotsRef: React.RefObject<HTMLDivElement | null>
) => {
  console.log('Card clicked:', clickedCard);
  if (gameState.isGameOver || gameState.isGameWon) return;
  
  // Check if slots are full
  if (gameState.slots.length >= MAX_SLOTS) {
    setGameState((prevState: GameState) => ({
      ...prevState,
      isGameOver: true
    }));
    return;
  }

  // Get card's DOM element
  const cardElement = document.getElementById(clickedCard.id);
  
  if (cardElement) {
    // Mark card as visually selected immediately
    const updatedCards = gameState.cards.map(card => 
      card.id === clickedCard.id ? { ...card, selected: true } : card
    );
    
    setGameState((prevState: GameState) => ({
      ...prevState,
      cards: updatedCards
    }));
    
    // Animate card to slot and update state when animation completes
    animateCardToSlot(cardElement, clickedCard.type, slotsRef, () => {
      const newSlot: SlotCard = {
        id: clickedCard.id,
        type: clickedCard.type
      };
      
      setGameState((prevState: GameState) => ({
        ...prevState,
        slots: [...prevState.slots, newSlot],
        lastMove: {
          type: LastMoveType.BOARD_CARD,
          cardId: clickedCard.id,
          cardType: clickedCard.type
        },
        canRevert: true
      }));
    });
  } else {
    // Fallback if we can't get the element
    const updatedCards = gameState.cards.map(card => 
      card.id === clickedCard.id ? { ...card, selected: true } : card
    );

    const newSlot: SlotCard = {
      id: clickedCard.id,
      type: clickedCard.type
    };

    setGameState({
      ...gameState,
      cards: updatedCards,
      slots: [...gameState.slots, newSlot],
      lastMove: {
        type: LastMoveType.BOARD_CARD,
        cardId: clickedCard.id,
        cardType: clickedCard.type
      },
      canRevert: true
    });
  }
};

/**
 * Handle when a card in the queue is clicked
 * @param card - The card that was clicked
 * @param queue - Which queue the card belongs to ('left' or 'right')
 * @param gameState - Current game state
 * @param setGameState - Function to update game state
 * @param slotsRef - Reference to the slots container
 */
export const handleQueueCardClick = (
  card: QueueCard,
  queue: 'left' | 'right',
  gameState: GameState,
  setGameState: (state: GameState | ((prevState: GameState) => GameState)) => void,
  slotsRef: React.RefObject<HTMLDivElement | null>
) => {
  console.log('Queue card clicked:', card, 'from queue:', queue);
  if (gameState.isGameOver || gameState.isGameWon) return;
  
  // Check if slots are full
  if (gameState.slots.length >= MAX_SLOTS) {
    setGameState({
      ...gameState,
      isGameOver: true
    });
    return;
  }

  // Get queue card's DOM element
  const queueElement = document.querySelector(`.card-queue.${queue} .queue-card`);
  
  if (queueElement) {
    // Save the current queue for potential reversion
    const queueCopy = queue === 'left' 
      ? [...gameState.leftQueue]
      : [...gameState.rightQueue];
    
    // Update appropriate queue immediately
    if (queue === 'left') {
      setGameState((prevState: GameState) => ({
        ...prevState,
        leftQueue: [...prevState.leftQueue.slice(1)]
      }));
    } else {
      setGameState((prevState: GameState) => ({
        ...prevState,
        rightQueue: [...prevState.rightQueue.slice(1)]
      }));
    }
    
    // Animate card to slot and update state when animation completes
    animateCardToSlot(queueElement, card.type, slotsRef, () => {
      const newSlot: SlotCard = {
        id: card.id,
        type: card.type
      };
      
      setGameState((prevState: GameState) => ({
        ...prevState,
        slots: [...prevState.slots, newSlot],
        lastMove: {
          type: queue === 'left' ? LastMoveType.LEFT_QUEUE : LastMoveType.RIGHT_QUEUE,
          cardId: card.id,
          cardType: card.type,
          queueCard: queueCopy[0]
        },
        canRevert: true
      }));
    });
  } else {
    // Fallback without animation
    const newSlot: SlotCard = {
      id: card.id,
      type: card.type
    };

    // Update state by removing the card from the queue
    if (queue === 'left') {
      const updatedLeftQueue = [...gameState.leftQueue.slice(1)];
      setGameState({
        ...gameState,
        slots: [...gameState.slots, newSlot],
        leftQueue: updatedLeftQueue,
        lastMove: {
          type: LastMoveType.LEFT_QUEUE,
          cardId: card.id,
          cardType: card.type,
          queueCard: gameState.leftQueue[0]
        },
        canRevert: true
      });
    } else {
      const updatedRightQueue = [...gameState.rightQueue.slice(1)];
      setGameState({
        ...gameState,
        slots: [...gameState.slots, newSlot],
        rightQueue: updatedRightQueue,
        lastMove: {
          type: LastMoveType.RIGHT_QUEUE,
          cardId: card.id,
          cardType: card.type,
          queueCard: gameState.rightQueue[0]
        },
        canRevert: true
      });
    }
  }
};

/**
 * Handle using the revival option after game over
 * @param gameState - Current game state
 * @param setGameState - Function to update game state
 */
export const handleRevive = (
  gameState: GameState,
  setGameState: (state: GameState | ((prevState: GameState) => GameState)) => void
) => {
  if (gameState.revivalChances <= 0 || !gameState.isGameOver) return;
  
  setGameState((prevState: GameState) => ({
    ...prevState,
    slots: [],
    isGameOver: false,
    revivalChances: prevState.revivalChances - 1 // Decrease remaining chances
  }));
};

/**
 * Handle the revert action for undoing the last move
 * @param gameState - Current game state
 * @param setGameState - Function to update game state
 */
export const handleRevert = (
  gameState: GameState,
  setGameState: (state: GameState | ((prevState: GameState) => GameState)) => void
) => {
  if (!gameState.canRevert || !gameState.lastMove || gameState.usedRevert) return;
  
  // Apply animation to the last card in the slot before reverting
  const lastSlotIndex = gameState.slots.length - 1;
  if (lastSlotIndex >= 0) {
    // Try to select the last card using a more specific selector
    const slotCard = document.querySelector(`.slot-card[data-index="${lastSlotIndex}"]`);
    
    if (slotCard) {
      console.log('Found slot card to animate:', slotCard);
      // Add revert animation class
      slotCard.classList.add('revert-animation');
      
      // Wait for animation to complete before reverting
      setTimeout(() => {
        // Remove animation class
        slotCard.classList.remove('revert-animation');
        
        // Execute the revert
        executeRevert(gameState, setGameState);
      }, 300); // Match the animation duration
    } else {
      // Try an alternative selector
      const slotCards = document.querySelectorAll('.slot-card');
      if (slotCards && slotCards.length > 0) {
        const lastCard = slotCards[slotCards.length - 1];
        console.log('Found slot card with alternative selector:', lastCard);
        
        lastCard.classList.add('revert-animation');
        
        setTimeout(() => {
          lastCard.classList.remove('revert-animation');
          executeRevert(gameState, setGameState);
        }, 300);
      } else {
        // Fallback if animation can't be applied
        console.log('Could not find slot card to animate, using fallback');
        executeRevert(gameState, setGameState);
      }
    }
  } else {
    // Fallback if no slot cards found
    executeRevert(gameState, setGameState);
  }
};

/**
 * Execute the revert action (internal helper function)
 * @param gameState - Current game state
 * @param setGameState - Function to update game state
 */
const executeRevert = (
  gameState: GameState,
  setGameState: (state: GameState | ((prevState: GameState) => GameState)) => void
) => {
  const { lastMove } = gameState;
  
  // If lastMove is null, we shouldn't proceed
  if (!lastMove) return;
  
  // Create a new state based on the last move type
  let newState = { 
    ...gameState, 
    canRevert: false,
    usedRevert: true  // Mark revert as used for the entire game
  };
  
  switch (lastMove.type) {
    case LastMoveType.BOARD_CARD:
      // Remove card from slots
      newState.slots = newState.slots.slice(0, -1);
      
      // Unselect the card in the board
      newState.cards = newState.cards.map(card => 
        card.id === lastMove.cardId ? { ...card, selected: false } : card
      );
      break;
      
    case LastMoveType.LEFT_QUEUE:
      // Remove card from slots
      newState.slots = newState.slots.slice(0, -1);
      
      // Put card back at the beginning of the left queue
      if (lastMove.queueCard) {
        newState.leftQueue = [lastMove.queueCard, ...newState.leftQueue];
      }
      break;
      
    case LastMoveType.RIGHT_QUEUE:
      // Remove card from slots
      newState.slots = newState.slots.slice(0, -1);
      
      // Put card back at the beginning of the right queue
      if (lastMove.queueCard) {
        newState.rightQueue = [lastMove.queueCard, ...newState.rightQueue];
      }
      break;
  }
  
  // Update the game state
  setGameState(newState);
};

/**
 * Handle reshuffling remaining cards on the board
 * @param gameState - Current game state
 * @param setGameState - Function to update game state
 * @param cardsContainerRef - Reference to the cards container
 */
export const handleReshuffle = (
  gameState: GameState,
  setGameState: (state: GameState | ((prevState: GameState) => GameState)) => void,
  cardsContainerRef: React.RefObject<HTMLDivElement | null>
) => {
  if (gameState.usedReshuffle) return;
  
  // Create a deep copy of the current cards
  const currentCards = [...gameState.cards];
  
  // Separate selected and unselected cards
  const selectedCards = currentCards.filter(card => card.selected);
  const unselectedCards = currentCards.filter(card => !card.selected);
  
  if (unselectedCards.length <= 1) {
    // Not enough cards to reshuffle
    return;
  }
  
  // Apply animation to cards container
  const cardsContainer = cardsContainerRef.current;
  if (cardsContainer) {
    cardsContainer.classList.add('container-ripple');
  }
  
  // Apply animation class to all unselected cards
  unselectedCards.forEach(card => {
    const cardElement = document.getElementById(card.id);
    if (cardElement) {
      // Add reshuffle animation class
      cardElement.classList.add('reshuffle-animation');
    }
  });
  
  // Wait for animation to start before doing the actual shuffle
  setTimeout(() => {
    // Shuffle the positions of unselected cards
    const shuffledUnselectedCards = [...unselectedCards];
    
    // Fisher-Yates shuffle algorithm for positions only
    const positions = shuffledUnselectedCards.map(card => ({ ...card.position }));
    for (let i = positions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [positions[i], positions[j]] = [positions[j], positions[i]];
    }
    
    // Assign new positions to the unselected cards
    for (let i = 0; i < shuffledUnselectedCards.length; i++) {
      shuffledUnselectedCards[i].position = positions[i];
    }
    
    // Recalculate which cards block which
    const newCards = [...selectedCards, ...shuffledUnselectedCards];
    
    // Clear all existing blockedBy arrays
    newCards.forEach(card => {
      if (!card.selected) {
        card.blockedBy = [];
      }
    });
    
    // Recalculate blockedBy for each card
    for (let i = 0; i < newCards.length; i++) {
      const card = newCards[i];
      
      // Skip selected cards
      if (card.selected) continue;
      
      // Find cards that might block this card
      for (let j = 0; j < newCards.length; j++) {
        const potentialBlocker = newCards[j];
        
        // Skip if it's the same card, if it's selected, or if it's in a lower layer
        if (
          i === j || 
          potentialBlocker.selected ||
          potentialBlocker.layer <= card.layer
        ) {
          continue;
        }
        
        // Check if the cards overlap
        const pos1 = card.position;
        const pos2 = potentialBlocker.position;
        
        // Calculate card centers
        const center1 = {
          x: pos1.x + 40, // Half of CARD_WIDTH
          y: pos1.y + 40  // Half of CARD_HEIGHT
        };
        
        const center2 = {
          x: pos2.x + 40,
          y: pos2.y + 40
        };
        
        // Distance between centers
        const dx = Math.abs(center1.x - center2.x);
        const dy = Math.abs(center1.y - center2.y);
        
        // If they overlap, add to blockedBy
        if (dx < 60 && dy < 60) { // 80 - 20 (CARD_WIDTH - CARD_OVERLAP/2)
          card.blockedBy.push(potentialBlocker.id);
        }
      }
    }
    
    // Update visibility based on blockers
    newCards.forEach(card => {
      if (!card.selected) {
        card.visible = card.blockedBy.length === 0;
      }
    });
    
    // Wait for animation to complete before updating state
    setTimeout(() => {
      // Update the game state with new card positions
      setGameState({
        ...gameState,
        cards: newCards,
        usedReshuffle: true
      });
      
      // Remove animation class from all cards
      unselectedCards.forEach(card => {
        const cardElement = document.getElementById(card.id);
        if (cardElement) {
          cardElement.classList.remove('reshuffle-animation');
        }
      });
      
      // Remove container animation
      if (cardsContainer) {
        cardsContainer.classList.remove('container-ripple');
      }
    }, 300); // Match the animation duration
  }, 50); // Small delay to ensure DOM updates before animation starts
};

/**
 * Handle moving out three rightmost cards to the invisible slot
 * @param gameState - Current game state
 * @param setGameState - Function to update game state
 * @param invisibleSlotsRef - Reference to the invisible slots container
 * @param setInvisibleSlot - Function to update the invisible slot state
 */
export const handleMoveOut = (
  gameState: GameState,
  setGameState: (state: GameState | ((prevState: GameState) => GameState)) => void,
  invisibleSlotsRef: React.RefObject<HTMLDivElement | null>,
  setInvisibleSlot: (cards: SlotCard[]) => void
) => {
  if (gameState.usedMoveOut) return; // Can only be used once per game
  
  const { slots } = gameState;
  
  // Need at least 3 cards in the slot to move out
  if (slots.length < 3) {
    console.log('Not enough cards in slot to move out');
    return;
  }
  
  // Get the three rightmost cards (most recently added)
  const cardsToMoveOut = slots.slice(-3).map((card, index) => ({
    // Create deep copies with unique IDs to prevent conflicts
    id: `invisible-${card.id}-${index}`,
    type: card.type
  }));
  console.log('Moving out cards to invisible slot:', cardsToMoveOut);
  
  // Get the remaining cards in the slot
  const remainingSlotCards = slots.slice(0, -3);
  
  // First ensure invisible slot container is visible but with no cards yet
  if (invisibleSlotsRef.current) {
    invisibleSlotsRef.current.style.opacity = '1';
  }
  
  // Create fixed positions for the three invisible slot positions
  const fixedPositions = [
    { left: "calc(50% - 120px)" },
    { left: "calc(50% - 35px)" },
    { left: "calc(50% + 50px)" }
  ];
  
  // Get the three rightmost elements from the main slot
  const slotCardElements = document.querySelectorAll('.slot-area .slot-card');
  const rightmostThreeElements: HTMLElement[] = [];
  
  // Get the three rightmost elements
  for (let i = slotCardElements.length - 1; i >= Math.max(0, slotCardElements.length - 3); i--) {
    rightmostThreeElements.unshift(slotCardElements[i] as HTMLElement);
  }
  
  // Create flying cards that animate from slot position to invisible slot position
  rightmostThreeElements.forEach((element, index) => {
    // Get the source card position
    const rect = element.getBoundingClientRect();
    
    // Get the current scroll position
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    
    // Calculate absolute position
    const top = rect.top + scrollTop;
    const left = rect.left + scrollLeft;
    
    // Create a clone of the card to animate
    const clone = element.cloneNode(true) as HTMLElement;
    clone.style.position = 'absolute';
    clone.style.top = `${top}px`;
    clone.style.left = `${left}px`;
    clone.style.width = `${rect.width}px`;
    clone.style.height = `${rect.height}px`;
    clone.style.margin = '0';
    clone.style.padding = '0';
    clone.style.transition = 'all 400ms ease-out';
    clone.style.zIndex = '1000';
    clone.style.transform = 'none';
    
    // Add to document body
    document.body.appendChild(clone);
    
    // Calculate the target position in the invisible slot
    const targetContainer = invisibleSlotsRef.current;
    if (targetContainer) {
      const targetRect = targetContainer.getBoundingClientRect();
      
      // Position is centered in the invisible slot container + offset for each card position
      const targetWidth = 70; // Width of card in invisible slot
      const targetHeight = 70; // Height of card in invisible slot
      
      // Calculate the final position
      let finalLeft = targetRect.left + scrollLeft;
      
      // Add the offset based on the fixed position
      if (fixedPositions[index].left.includes('calc')) {
        // Extract the offset value from calc string
        const offsetMatch = fixedPositions[index].left.match(/calc\(50% ([+-]) (\d+)px\)/);
        if (offsetMatch) {
          const operator = offsetMatch[1]; // '+' or '-'
          const value = parseInt(offsetMatch[2]);
          finalLeft = targetRect.left + scrollLeft + (targetRect.width / 2);
          finalLeft = operator === '+' ? finalLeft + value : finalLeft - value;
          finalLeft -= (targetWidth / 2); // Adjust for the card width
        }
      }
      
      const finalTop = targetRect.top + scrollTop + (targetRect.height - targetHeight) / 2;
      
      // First make the original element invisible
      element.style.opacity = '0';
      
      // After a small delay to ensure the clone is visible
      setTimeout(() => {
        // Animate the clone to target position
        clone.style.top = `${finalTop}px`;
        clone.style.left = `${finalLeft}px`;
        clone.style.width = `${targetWidth}px`;
        clone.style.height = `${targetHeight}px`;
        clone.style.transform = 'scale(1.05)'; // Slightly larger to match invisible card style
        
        // Remove the clone after animation completes
        setTimeout(() => {
          clone.remove();
          
          // Only set the invisible slot cards after all animations complete
          if (index === 2) { // Last card
            // Update the game state to remove cards from the regular slot
            setGameState(prevState => ({
              ...prevState,
              slots: remainingSlotCards,
              usedMoveOut: true
            }));
            
            // Add cards to invisible slot
            setInvisibleSlot(cardsToMoveOut);
            
            // Make the invisible slot cards clickable after a short delay
            setTimeout(() => {
              const invisibleSlotCards = invisibleSlotsRef.current?.querySelectorAll('.invisible-slot-card');
              invisibleSlotCards?.forEach(card => {
                card.classList.add('clickable');
              });
              console.log('Made all invisible cards clickable');
            }, 100);
          }
        }, 400);
      }, 10);
    }
  });
}; 